import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  TrendingUp,
  Trophy,
  LineChart,
  ShieldCheck,
  Rocket,
  Lightbulb,
  Wrench,
  Cog,
  FlaskConical,
  MapPin,
  Sparkles,
} from 'lucide-react';
import { Reveal, useSheen } from './Reveal';

const iconMap = { Building2, TrendingUp, Trophy, LineChart, ShieldCheck, Rocket, Lightbulb, Wrench, Cog, FlaskConical };

const accentMap = {
  amber: { ring: 'bg-amber-500/15', icon: 'text-amber-400', text: 'text-amber-400', dot: 'bg-amber-400', glow: 'shadow-[0_0_60px_-15px_rgba(245,158,11,0.5)]' },
  blue: { ring: 'bg-blue-500/15', icon: 'text-blue-400', text: 'text-blue-400', dot: 'bg-blue-400', glow: 'shadow-[0_0_60px_-15px_rgba(96,165,250,0.5)]' },
  green: { ring: 'bg-emerald-500/15', icon: 'text-emerald-400', text: 'text-emerald-400', dot: 'bg-emerald-400', glow: 'shadow-[0_0_60px_-15px_rgba(52,211,153,0.5)]' },
  purple: { ring: 'bg-violet-500/15', icon: 'text-violet-400', text: 'text-violet-400', dot: 'bg-violet-400', glow: 'shadow-[0_0_60px_-15px_rgba(167,139,250,0.5)]' },
};

const CaseStudies = ({ data }) => {
  const [active, setActive] = useState(0);
  const current = data[active];
  const Icon = iconMap[current.icon] || Trophy;
  const acc = accentMap[current.accent] || accentMap.amber;

  const prev = () => setActive((a) => (a - 1 + data.length) % data.length);
  const next = () => setActive((a) => (a + 1) % data.length);
  const { onMouseMove } = useSheen();

  return (
    <section id="testimonials" className="relative py-24 lg:py-32 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="section-num mb-5">
              <span className="num">06</span>
              <span>Verified Outcomes</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              <span className="text-white">Outcomes That </span>
              <span className="text-gradient-mixed">Boards Can Reference</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl leading-relaxed">
              Anonymised engagements across industries — each metric is drawn from a real
              completed contract. Client identities remain protected under NDA, available on
              request for mutual confidentiality review.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous"
              className="w-12 h-12 rounded-full btn-ghost-dark inline-flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              className="w-12 h-12 rounded-full btn-ghost-dark inline-flex items-center justify-center"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Featured case study */}
          <Reveal variant="left" className="lg:col-span-2">
            <div
              key={current.id}
              onMouseMove={onMouseMove}
              className={`card-sheen relative glass-card rounded-3xl p-8 lg:p-12 min-h-[480px] flex flex-col overflow-hidden ${acc.glow}`}
            >
            {/* Decorative gradient blobs */}
            <div
              className="absolute -top-32 -right-32 w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
              style={{
                background:
                  current.accent === 'amber'
                    ? 'radial-gradient(circle, #F59E0B, transparent 70%)'
                    : current.accent === 'blue'
                    ? 'radial-gradient(circle, #3B82F6, transparent 70%)'
                    : current.accent === 'green'
                    ? 'radial-gradient(circle, #10B981, transparent 70%)'
                    : 'radial-gradient(circle, #8B5CF6, transparent 70%)',
              }}
            />

            <div className="relative flex items-center justify-between mb-8 animate-fade-up">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold ${acc.text} bg-white/5 border border-white/10`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${acc.dot} pulse-dot`} />
                  {current.category}
                </span>
                <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-400">
                  <MapPin className="w-3.5 h-3.5" />
                  {current.region}
                </span>
              </div>
              <span className="text-xs text-slate-500 font-mono">
                {String(active + 1).padStart(2, '0')}<span className="text-slate-700"> / </span>{String(data.length).padStart(2, '0')}
              </span>
            </div>

            {/* Industry tag */}
            <div className="relative flex items-center gap-4 mb-8 animate-fade-up">
              <div className={`w-14 h-14 rounded-2xl ${acc.ring} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-6 h-6 ${acc.icon}`} />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
                  Industry
                </div>
                <div className="text-xl font-bold text-white">{current.industry}</div>
              </div>
            </div>

            {/* Big metric */}
            <div className="relative mb-7 animate-fade-up">
              <div className={`text-6xl lg:text-7xl font-extrabold tracking-tight mb-2 ${acc.text}`}>
                {current.metric}
              </div>
              <div className="text-slate-400 text-sm uppercase tracking-[0.2em] font-semibold">
                {current.metricLabel}
              </div>
            </div>

            <p className="relative text-base lg:text-lg text-slate-200 leading-relaxed max-w-2xl animate-fade-up">
              {current.summary}
            </p>

            {/* Highlights */}
            <div className="relative mt-auto pt-8 grid grid-cols-3 gap-4 border-t border-white/5">
              {current.highlights.map((h, i) => (
                <div key={i}>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1.5">
                    {h.k}
                  </div>
                  <div className="text-base lg:text-lg font-bold text-white">{h.v}</div>
                </div>
              ))}
            </div>

            <div className="relative mt-6 flex items-center gap-2 text-[11px] text-slate-500">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              Engagement details verified internally · Client name protected under NDA
            </div>
            </div>
          </Reveal>

          {/* Side list of other case studies */}
          <Reveal variant="right" className="flex flex-col gap-3">
            {data.map((c, i) => {
              const isActive = i === active;
              const a = accentMap[c.accent] || accentMap.amber;
              const ItemIcon = iconMap[c.icon] || Trophy;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActive(i)}
                  onMouseMove={onMouseMove}
                  className={`card-sheen text-left glass-card rounded-2xl p-5 flex items-center gap-4 transition-colors ${
                    isActive ? 'border-amber-500/40 bg-amber-500/5' : 'glass-card-hover'
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl ${a.ring} flex items-center justify-center flex-shrink-0`}>
                    <ItemIcon className={`w-5 h-5 ${a.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white text-sm truncate">{c.industry}</div>
                    <div className="text-xs text-slate-400 truncate flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      {c.region} · {c.category}
                    </div>
                  </div>
                  <div className={`font-extrabold text-sm whitespace-nowrap ${a.text}`}>
                    {c.metric}
                  </div>
                </button>
              );
            })}
          </Reveal>
        </div>

        {/* Pagination dots */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {data.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? 'w-8 bg-amber-400' : 'w-1.5 bg-white/20 hover:bg-white/40'
              }`}
              aria-label={`Show case study ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
